import { calculateSimilarityScore } from '@chronosphereio/ai';
import { useIsAiEnabled } from '@chronosphereio/core';
import { useCallback, useEffect, useRef } from 'react';
import { useAnalytics, useDataQueryEditor } from '@/plugin-system';
import { useVizFrameworkConfig } from '@/plugin-system/model/config';
import { useQueryAssistant } from './query-assistant-context';

export function useIsQueryAssistantEnabled() {
  const config = useVizFrameworkConfig();
  const isAiEnabled = useIsAiEnabled();
  const suggestQueryFn = useSuggestQueryFn();
  // ai promql generation requires both the gateway-level ai config and the feature flag
  return isAiEnabled && (config?.featureFlags.aiPromQLQueryGeneration ?? false) && Boolean(suggestQueryFn);
}

export function useSuggestQueryFn() {
  const dataQueryEditorCtx = useDataQueryEditor();
  return dataQueryEditorCtx?.onSuggestQuery;
}

/**
 * Add a keyboard shortcut to toggle the query assistant: `Cmd + I`.
 *
 * Returns a `containerRef` that callers must attach to their query editor.
 */
export function useQueryAssistantShortcut() {
  const isAssistantEnabled = useIsQueryAssistantEnabled();
  const { isAssistantOpen, openAssistant, closeAssistant } = useQueryAssistant();

  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!containerRef.current?.contains(document.activeElement)) return;

      if (e.metaKey && e.key === 'i') {
        e.preventDefault();
        e.stopPropagation();
        if (isAssistantOpen) {
          closeAssistant();
        } else {
          openAssistant();
        }
      }
    },
    [isAssistantOpen, openAssistant, closeAssistant]
  );

  useEffect(() => {
    if (!isAssistantEnabled) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, isAssistantEnabled]);

  return { containerRef };
}

/**
 * Get `onAccept` and `onReject` callbacks for the query assistant.
 */
export function useQueryAssistantAcceptAndReject({ feature, evalName }: { feature: string; evalName: string }) {
  const analytics = useAnalytics();
  const { query, suggestion } = useQueryAssistant();

  const onAccept = useCallback(() => {
    if (!suggestion || !analytics?.trackEval) return;

    const currentQuery = query;
    const suggestedQuery = suggestion?.query ?? '';
    const { version, input, existingQuery } = suggestion?.metadata ?? {};

    analytics.trackEval({
      feature,
      input: input,
      output: suggestedQuery,
      eval: { name: evalName, score: calculateSimilarityScore(suggestedQuery, currentQuery) / 100 },
      version: version ?? '0',
      metadata: { existingQuery },
    });
  }, [suggestion, analytics, feature, evalName, query]);

  const onReject = useCallback(() => {
    if (!suggestion || !analytics?.trackEval) return;

    const { version, input, existingQuery } = suggestion?.metadata ?? {};

    analytics.trackEval({
      feature,
      input: input,
      output: suggestion.query,
      eval: { name: evalName, score: 0 },
      version: version ?? '0',
      metadata: { existingQuery },
    });
  }, [suggestion, analytics, feature, evalName]);

  return { onAccept, onReject };
}
